import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatPhone } from '#/shared/lib/format'
import { openSmsComposer } from '#/shared/lib/sms-composer'
import type { SmsComposerTarget } from '#/shared/lib/sms-composer'
import { useStartCall } from '#/shared/lib/telephony'
import { cn } from '#/shared/lib/utils'
import { CopyIcon, PhoneIcon, SmsIcon } from '#/shared/ui/icons'
import {
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '#/shared/ui/tooltip'

function PhoneActionButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
      className="flex cursor-pointer items-center gap-1.25 rounded px-1.5 py-1 font-medium text-white transition-colors hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {icon}
      {label}
    </button>
  )
}

/** Copies a phone number, briefly flipping the label to "Copied". */
function useCopyPhone(value: string) {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    [],
  )

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      return
    }
    setCopied(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setCopied(false), 1500)
  }

  return { copied, copy }
}

interface CallablePhoneProps {
  value: string
  className?: string
  /**
   * When set, the number reads as a one-tap call: a leading phone icon shakes
   * on hover and clicking the icon or number dials immediately. The hover
   * tooltip then offers "SMS" (opens the composer pre-filled with this target)
   * and "Copy" instead of "Call". Without it the number is select-only and the
   * tooltip offers "Call"/"Copy" — avoiding accidental calls.
   */
  smsTarget?: SmsComposerTarget
  /**
   * Hide the leading call icon while keeping the one-tap-call behaviour and the
   * SMS/Copy hover tooltip. Used where the surrounding layout already conveys
   * "phone" (e.g. the customer card) and a second icon would be redundant.
   */
  hideIcon?: boolean
}

function CallTooltipActions({
  value,
  copied,
  onCopy,
}: {
  value: string
  copied: boolean
  onCopy: () => void
}) {
  const { t } = useTranslation()
  const { call, ready } = useStartCall()
  return (
    <>
      <PhoneActionButton
        icon={<PhoneIcon className="size-3.5" />}
        label={t('phone.actions.call')}
        disabled={!ready}
        onClick={() => call(value)}
      />
      <span aria-hidden className="h-4 w-px bg-white/20" />
      <PhoneActionButton
        icon={<CopyIcon className="size-3.5" />}
        label={copied ? t('phone.actions.copied') : t('phone.actions.copy')}
        onClick={onCopy}
      />
    </>
  )
}

function SmsTooltipActions({
  target,
  copied,
  onSms,
  onCopy,
}: {
  target: SmsComposerTarget
  copied: boolean
  onSms: (target: SmsComposerTarget) => void
  onCopy: () => void
}) {
  const { t } = useTranslation()
  return (
    <>
      <PhoneActionButton
        icon={<SmsIcon className="size-3.5" />}
        label={t('phone.actions.sms')}
        onClick={() => onSms(target)}
      />
      <span aria-hidden className="h-4 w-px bg-white/20" />
      <PhoneActionButton
        icon={<CopyIcon className="size-3.5" />}
        label={copied ? t('phone.actions.copied') : t('phone.actions.copy')}
        onClick={onCopy}
      />
    </>
  )
}

/** The number itself: a static span by default, or a click-to-dial button when
 *  a one-tap call target is set. */
function PhoneLabel({
  value,
  className,
  onCall,
}: {
  value: string
  className?: string
  onCall?: () => void
}) {
  const { t } = useTranslation()
  const base =
    'min-w-0 cursor-pointer truncate text-left transition-colors select-none hover:text-blue-500'
  if (!onCall) {
    return <span className={cn(base, className)}>{formatPhone(value)}</span>
  }
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        onCall()
      }}
      aria-label={t('phone.callAria', { number: formatPhone(value) })}
      className={cn(base, className)}
    >
      {formatPhone(value)}
    </button>
  )
}

/** A phone number that reveals quick actions on hover instead of dialing on a
 *  single click. With {@link CallablePhoneProps.smsTarget} it becomes a one-tap
 *  call with an SMS shortcut; without it the tooltip offers an explicit "Call". */
export function CallablePhone({
  value,
  className,
  smsTarget,
  hideIcon,
}: CallablePhoneProps) {
  const { t } = useTranslation()
  const { call } = useStartCall()
  const { copied, copy } = useCopyPhone(value)
  // Controlled so we can force-close it when an action opens a modal/dialog —
  // Radix otherwise keeps the tooltip open since the pointer never leaves the
  // trigger before focus moves to the dialog.
  const [open, setOpen] = useState(false)
  const onCopy = () => void copy()
  const onSms = (target: SmsComposerTarget) => {
    setOpen(false)
    openSmsComposer(target)
  }

  return (
    <TooltipProvider delayDuration={150}>
      <TooltipRoot open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <span className="group inline-flex min-w-0 items-center gap-1.25">
            {smsTarget && !hideIcon && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  call(value)
                }}
                aria-label={t('phone.callAria', { number: formatPhone(value) })}
                className="flex shrink-0 cursor-pointer items-center text-primary"
              >
                <PhoneIcon className="size-4.5 origin-center group-hover:animate-phone-ring" />
              </button>
            )}
            <PhoneLabel
              value={value}
              className={className}
              onCall={smsTarget ? () => call(value) : undefined}
            />
          </span>
        </TooltipTrigger>
        <TooltipContent
          // z-[80] (popout layer): float above the modal layer (z-70) and the
          // detail sidebar (z-60) so the actions stay visible when a phone field
          // is rendered inside either. See the scale in app/styles/index.css.
          className="z-[80] flex items-center gap-0.5 p-1"
          showArrow
        >
          {smsTarget ? (
            <SmsTooltipActions
              target={smsTarget}
              copied={copied}
              onSms={onSms}
              onCopy={onCopy}
            />
          ) : (
            <CallTooltipActions value={value} copied={copied} onCopy={onCopy} />
          )}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  )
}
